import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getWebBuilderDetails } from '../api'
import { useAiCompanionModeStore } from '@/state/selection.store'

/**
 * Hook to fetch and manage AI Companion Mode for a project
 * Automatically updates the global store when fetched
 * 
 * Usage:
 * ```jsx
 * import { useAiCompanionMode } from '@/features/site-pages/hooks/useAiCompanionMode'
 * 
 * function MyComponent() {
 *   const { isAiCompanionMode } = useAiCompanionMode(uniqueUrl)
 *   
 *   return (
 *     {isAiCompanionMode && <AiCompanionFeature />}
 *   )
 * }
 * ```
 * 
 * For direct store access (without fetching):
 * ```jsx
 * import { useAiCompanionModeStore } from '@/state/selection.store'
 * 
 * function MyComponent() {
 *   const { isAiCompanionMode } = useAiCompanionModeStore()
 *   // Use cached value from store
 * }
 * ```
 */
export const useAiCompanionMode = (uniqueUrl) => {
  const { isAiCompanionMode: storeValue, setAiCompanionMode } = useAiCompanionModeStore()

  const { data, isLoading } = useQuery({
    queryKey: ['aiCompanionMode', uniqueUrl],
    queryFn: async () => {
      if (!uniqueUrl) return false
      const response = await getWebBuilderDetails(uniqueUrl, 'home', 'builder')
      const aiMode = response?.aiCompanionMode ?? response?.data?.aiCompanionMode ?? false
      console.log('🔍 AI Companion Mode:', uniqueUrl, '→', aiMode)
      return aiMode
    },
    enabled: !!uniqueUrl,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    placeholderData: storeValue, 
  })

  // Sync fetched data to global store (one-way: query → store)
  // Do NOT include storeValue in deps — it creates a cycle: data→set→storeValue→effect→set
  useEffect(() => {
    if (data !== undefined) {
      setAiCompanionMode(data)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  return {
    isAiCompanionMode: data ?? storeValue ?? false,
    isLoading,
  }
}

